import { watchEffect } from "vue";

export default class Acl {
  store = null;

  isReady = false;

  isReadyPromise = false;

  permissions = {};

  isSuperAdmin = false;

  conditions = ["all", "own", "some"];

  userGroups = [];

  userGroupIds = [];

  // which type of permission key yields which type of client ACL level
  permissionConditionsMap = {
    any: "all",
    own: "own",
    ownThrough: "some",
    ownThroughMany: "some",
    callback: "some",
  };

  // which level is "better" than which
  permissionLevelMap = {
    some: [],
    own: ["some"],
    all: ["own", "some"],
  };

  permissionSatisfactionLevelMap = {
    all: ["all"],
    own: ["all", "own", "ownThrough", "ownThroughMany", "ownThroughAny"],
    some: [
      "some",
      "own",
      "ownThrough",
      "ownThroughMany",
      "ownThroughAny",
      "all",
    ],
  };

  actionsWithNoFields = ["deleteVersion", "delete", "assign"];

  constructor(store, userState = false) {
    // default behaviour - track main user in state
    this.store = store;

    let attachToMainUser = () => {
      this.isReadyPromise =
        this.store.getters["user/initialFetchCompletePromise"];

      watchEffect(
        () => {
          this.permissions = this.store.getters["user/permissions"];
          this.isSuperAdmin = this.store.getters["user/isSuperAdmin"];

          // track user groups from user profile, always adding the default "public", and sometimes the default "registered"  - if theres a user
          if (this.store.getters["user/hasProfile"]) {
            let profileGroups = this.store.getters["user/profile"].UserGroups;
            let mappedProfileGroups = Array.isArray(profileGroups)
              ? profileGroups.map((group) => group.alias)
              : [];
            let mappedProfileGroupIds = Array.isArray(profileGroups)
              ? profileGroups.map((group) => group.id)
              : [];
            this.userGroups = Array.from(
              new Set(["public", "registered", ...mappedProfileGroups])
            );

            this.userGroupIds = Array.from(new Set([...mappedProfileGroupIds]));
          } else {
            this.userGroups = ["public"];
            this.userGroupIds = [];
          }

          // common confusion between "superUser" and "superUsers"
          if (
            this.userGroups.includes("superUser") &&
            !this.userGroups.includes("superUsers")
          ) {
            this.userGroups.push("superUsers");
          }

          if (
            this.userGroups.includes("superUsers") &&
            !this.userGroups.includes("superUser")
          ) {
            this.userGroups.push("superUser");
          }

          if (this.store.getters["user/initialFetchComplete"]) {
            this.isReady = true;
          }
        } // immediate is required in SSR - becuase store will have the state and it will not change
      );
    };

    let attachToCustomUser = () => {
      this.isReadyPromise = new Promise((resolve) => resolve());

      watchEffect(() => {
        this.permissions = userState.permissions;
        this.isSuperAdmin = userState.user.isSuperAdmin;

        // track user groups from user profile, always adding the default "public", and sometimes the default "registered"  - if theres a user

        let profileGroups = userState.user.profile.UserGroups;
        let mappedProfileGroups = Array.isArray(profileGroups)
          ? profileGroups.map((group) => group.alias)
          : [];
        let mappedProfileGroupIds = Array.isArray(profileGroups)
          ? profileGroups.map((group) => group.id)
          : [];
        this.userGroups = Array.from(
          new Set(["public", "registered", ...mappedProfileGroups])
        );

        this.userGroupIds = Array.from(new Set([...mappedProfileGroupIds]));

        // common confusion between "superUser" and "superUsers"
        if (
          this.userGroups.includes("superUser") &&
          !this.userGroups.includes("superUsers")
        ) {
          this.userGroups.push("superUsers");
        }

        if (
          this.userGroups.includes("superUsers") &&
          !this.userGroups.includes("superUser")
        ) {
          this.userGroups.push("superUser");
        }

        this.isReady = true;
      });
    };

    if (!userState) {
      attachToMainUser();
    } else {
      attachToCustomUser();
    }
  }

  enforceLevelIntegrity(condition) {
    // enforce condition integrity
    if (!this.conditions.includes(condition)) {
      warn('ACL - canDetailed - invalid input condition, setting to "all"', {
        condition,
      });
      return "all";
    }

    return condition;
  }

  enforceFieldsIntegrity(fields) {
    if (fields === "*" || fields === "any" || fields === "some") {
      return ["*"];
    }
    if (
      Array.isArray(fields) &&
      fields.length === 1 &&
      (fields.includes("some") ||
        fields.includes("any") ||
        fields.includes("*"))
    ) {
      return ["*"];
    }
    // enforce fields integrity
    if (typeof fields === "string") {
      return [fields];
    }
    if (!Array.isArray(fields)) {
      warn("acl - invalid fields. must be array of strings, or a string", {
        fields,
      });
    }
    return fields;
  }

  enforceActionsIntegrity(actions) {
    if (typeof actions === "string") {
      return [actions];
    }
    if (!Array.isArray(actions)) {
      warn(
        "Actions provided to ACL plugin are invalid. Must be array of strings or a string denoting the action. Defaulting this is unsafe. Expect a fatal error",
        actions
      );
    }
    return actions;
  }

  enforceCanArgumentIntegrity(arg) {
    if (!arg || typeof arg !== "object") {
      warn("acl - enforceCanArgumentIntegrity invalid arg, must be object", {
        arg,
      });
      return {
        assetType: null,
        actions: null,
        fields: "*",
        level: "some",
      };
    }

    let result = {
      assetType: arg.assetType ?? null,
      actions: arg.actions ?? null,
      fields: arg.fields ?? "*",
      level: arg.level ?? "some",
    };

    // support aliases for "asset type"
    if (typeof arg.asset === "string") {
      result.assetType = arg.asset;
    }

    if (typeof arg.type === "string") {
      result.assetType = arg.type;
    }

    // allow a "key" (for canMany);
    result.key = arg.key ?? result.assetType;
    return result;
  }

  /**
   * Check if a user can do some action to assets of type X, with a detailed report
   * returns a boolean AND the "condition" of access (i.e. own, all, some)
   * notice that the type of asset does not appear on the report, because it we sent by the caller.
   *
   * @param assetType - string - what type of backend asset are we talking about? i.e. user, review,conversionRate
   * @param actions - string/array what actions are we performing (can be string for one action, or array for 1 or more actions), i.e. "read", or ["list","edit"]
   * @param fields -  string/array fields to be manipulated, i.e. "name" or ["id","name"],
   *                  special values: *,"any","some" - all mean that any field permission satisfies the request (any one field)
   * @param level - level of permissions required - "some","own" or "all"
   * Example for the response
   * {
   *   "report": {
   *      // action required
   *     "read": {
   *       // field - detail
   *       "title": {
   *         "can": true, // field final verdict
   *         "canSome": true, // can we under any case do this action to the field?
   *         "type": "all",  // actual maxuimum level of permission
   *         "required": "some" // original requirement
   *       },
   *     }
   *   }
   *   // for the entire request - whats the virdict? all of these are the same, for comfort of use
   *   "verdict": true,
   *   "can": true,
   *   "finalCan": true
   * }
   */
  canDetailed(assetType, actions, fields = "any", level = "some") {
    let results;

    let enforceInputIntegrity = () => {
      // one argument - object
      if (assetType && typeof assetType === "object") {
        let safe = this.enforceCanArgumentIntegrity(assetType);
        actions = this.enforceActionsIntegrity(safe.actions);
        level = this.enforceLevelIntegrity(safe.level);
        fields = this.enforceFieldsIntegrity(safe.fields);
        assetType = safe.assetType;
        return;
      }

      let split = typeof assetType === "string" ? assetType.split("|") : [];

      // multiple arguments, string denoting assetType
      if (split.length < 2) {
        actions = this.enforceActionsIntegrity(actions);
        level = this.enforceLevelIntegrity(level);
        fields = this.enforceFieldsIntegrity(fields);
        return;
      }

      // one string argument, denoting multiple arguments
      let assetTypeCandidate = split[0];
      let actionsCandidate = split[1]?.split(",") || undefined;
      let fieldsCandidate = split[2] || undefined;
      let levelCandidate = split[3] || undefined;
      assetType = String(assetTypeCandidate);
      actions = this.enforceActionsIntegrity(actionsCandidate);
      level = this.enforceLevelIntegrity(fieldsCandidate);
      fields = this.enforceFieldsIntegrity(levelCandidate);
    };

    let mapRequirements = (results) => {
      if (!results || typeof results !== "object") {
        results = {};
      }

      actions.forEach((action) => {
        results[action] = {};

        // actions that do not address specific fields are expressed as the *
        if (this.actionsWithNoFields.includes(action)) {
          results[action]["*"] = {
            can: this.isSuperAdmin,
            canSome: this.isSuperAdmin,
            type: this.isSuperAdmin ? "all" : false,
            required: level,
          };

          return true;
        }

        fields.forEach((field) => {
          results[action][field] = {
            can: this.isSuperAdmin,
            canSome: this.isSuperAdmin,
            type: this.isSuperAdmin ? "all" : false,
            required: level,
          };
        });
      });
      return results;
    };

    let appendVerdict = (results) => {
      let finalCan = true;
      // any false -> cant
      // for each results
      Object.values(results).forEach((action) => {
        let shouldContinue = true;
        Object.values(action).forEach((fieldReport) => {
          if (!fieldReport.can) {
            shouldContinue = false;
            finalCan = false;
            return false;
          }
        });
        if (!shouldContinue) {
          return false;
        }
      });

      return {
        report: results,
        verdict: finalCan,
        can: finalCan,
        finalCan: finalCan,
      };
    };

    // these three functions are using each other  to iterate the main permission tree.
    // the NODE uses the actions, and the action uses the fields to grant granular permissions per each field that might have been requestsd
    /**
     *  grant permissions based on a field in the permission tree
     * @param results - the results we list our grants in
     * @param field - the field (name) from the permission tree (i.e. 'id','rating' )
     * @param action - the action underwhich the field is listed  (i.e. 'read','list'),
     * @param permissionType  - the type of grant (raw) we are giving - all/own/some
     */
    let grantFieldPermissions = (results, field, action, permissionType) => {
      // if the field is not required - skip it
      if (!results[action][field]) {
        return;
      }

      // current grant level in the result
      let currentLevel = results[action][field].type;

      // list of all possible "lower" permission levels, which our grant can replace because it is "better"
      let overrides = [
        false,
        permissionType,
        ...this.permissionLevelMap[permissionType],
      ];

      // set can some to true - we are granting a permission, so it is at least "some"
      results[action][field].canSome = true;

      // if the current level of permissions => what we are granting - its done
      if (!overrides.includes(currentLevel)) {
        return;
      }

      // set the new "better" permission type
      results[action][field].type = permissionType;

      // check if the required level for the field is met
      let requiredLevel = results[action][field].required;
      let sufficientTypes = this.permissionSatisfactionLevelMap[requiredLevel];

      if (sufficientTypes.includes(permissionType)) {
        results[action][field].can = true;
      }
    };

    /**
     * Grant permissions by an action in the permissions tree
     * @param results - results to list the permissions in
     * @param action - name of the action (i.e. "read","list")
     * @param fields - array of the field names granted
     * @param permissionType  - the type of grant (raw) we are giving - all/own/some
     */
    let grantActionPermissions = (results, action, fields, permissionType) => {
      // make a full grant for the special pseudo field '*' - if any field grans this permission;
      if (results[action].hasOwnProperty("*") && fields.length > 0) {
        grantFieldPermissions(results, "*", action, permissionType);
      }

      // hande the "true" permissions, which exists in actions that are not field dependent, such as "delete"
      if (fields === true) {
        // warn the developer if something goes wrong. this should be handled by mapRequirements - it should detect such requests and map the * field, no matter what fields were passed to it
        if (!results[action]["*"]) {
          warn(
            'acl - canDetailed - grantActionPermissions - a "true" permission is detected in permission tree, but the * field is not set to express it as a requirement. check the ACL code',
            {
              results,
              action,
              fields,
              permissionType,
              permissions: this.permissions,
            }
          );
        }
        grantFieldPermissions(results, "*", action, permissionType);
        return;
      }
      fields.forEach((field) => {
        grantFieldPermissions(results, field, action, permissionType);
      });
    };

    /**
     * Grant permissions baesd on a type in the permissions tree (i.e. review.any, whic will contain {action: [fields]}
     * @param results - results to append to
     * @param permissionType - mapped permissions to grant, i.e. some/own/all
     * @param nodeContent - actions object - content of the nodeType
     */
    let assignNodeTypePermissions = (results, permissionType, nodeContent) => {
      for (const [action, fields] of Object.entries(nodeContent)) {
        // if action not in requirements skip
        if (!Object.keys(results).includes(action)) {
          continue;
        }
        grantActionPermissions(results, action, fields, permissionType);
      }
    };

    // standardize the input
    enforceInputIntegrity();

    // map the actions/fields
    results = mapRequirements();

    // cases where we dont need to check: super admin (all granted), no permission at all (all denied)
    if (this.isSuperAdmin || !this.permissions[assetType]) {
      return appendVerdict(results);
    }

    for (const [type, actions] of Object.entries(this.permissions[assetType])) {
      // "translate" the level of permissions granted by this (i.e. any -> all, ownThrough -> some, etc)
      let permissionType = this.permissionConditionsMap[type] ?? "some";
      // assign the permissions granted by this entry. this goes deep down to the field level, using 3 functions (above)
      assignNodeTypePermissions(results, permissionType, actions);
    }

    // the granular report is complete, add a verdict
    return appendVerdict(results);
  }

  /**
   * Run several checks for several assets - it passes if ALL the rules pass
   * @param rules - array of rules that can go into canDetailed
   *                 the result returns mapped by asset type. If you want to check different permisions for same asset, add a "key" property, so that you can get the report
   * @param detailed - should response come back as a report or as a boolean
   */
  canMany(rules = [], detailed = false) {
    let result = {};
    rules.forEach((rule) => {
      let safeRule = this.enforceCanArgumentIntegrity(rule);
      let ruleKey = safeRule.key;
      if (result[ruleKey]) {
        ruleKey = ruleKey + "-1";
      }

      result[ruleKey] = this.canDetailed(safeRule);
    });

    if (detailed) {
      return result;
    }

    // we need just bool verdict
    let verdict = true;
    Object.values(result).forEach((item) => {
      if (!item.can) {
        verdict = false;
        return false;
      }
    });

    return verdict;
  }

  /**
   * Run several checks for several assets - it passes if any of the rules passes
   * @param rules - array of rules that can go into canDetailed
   *                 the result returns mapped by asset type. If you want to check different permisions for same asset, add a "key" property, so that you can get the report
   * @param detailed - should response come back as a report or as a boolean
   */
  canAny(rules = [], detailed = false) {
    let result = {};
    rules.forEach((rule) => {
      let safeRule = this.enforceCanArgumentIntegrity(rule);
      let ruleKey = safeRule.key;
      if (result[ruleKey]) {
        ruleKey = ruleKey + "-1";
      }

      result[ruleKey] = this.canDetailed(safeRule);
    });

    if (detailed) {
      return result;
    }

    // we need just bool verdict
    let verdict = false;
    Object.values(result).forEach((item) => {
      if (item.can) {
        verdict = true;
        return false;
      }
    });

    return verdict;
  }

  /**
   * Run several checks for several assets - it passes if any of the rules passes ON ANY ACTION
   * @param rules - array of rules that can go into canDetailed
   *                 the result returns mapped by asset type. If you want to check different permisions for same asset, add a "key" property, so that you can get the report
   * @param detailed - should response come back as a report or as a boolean
   */
  canAnySoft(rules = [], detailed = false) {
    let result = {};
    rules.forEach((rule) => {
      let safeRule = this.enforceCanArgumentIntegrity(rule);
      let ruleKey = safeRule.key;
      if (result[ruleKey]) {
        ruleKey = ruleKey + "-1";
      }

      result[ruleKey] = this.canDetailed(safeRule);
    });

    if (detailed) {
      return result;
    }

    // we need just bool verdict
    let verdict = false;
    Object.values(result).forEach((ruleResult) => {
      Object.values(ruleResult.report).forEach((reportItem) => {
        Object.values(reportItem).forEach((reportSubItem) => {
          if (reportSubItem.can) {
            verdict = true;
          }
        });
      });
    });

    return verdict;
  }

  /**
   * A quick to use version of canDetailed, returning a simple true/false
   * @param assetType
   * @param actions
   * @param fields
   * @param level
   */
  can(assetType, actions, fields = "any", level = "some", detailed = false) {
    let res = this.canDetailed(assetType, actions, fields, level);
    if (detailed) {
      return res;
    }
    return res.can;
  }

  /**
   * Check if a user can do some action to all assets of type X
   */
  canAll(assetType, actions, fields = "any") {
    return this.can(assetType, actions, fields, "all");
  }

  /**
   * Check if a user can do some action to  assets of type X that the user owns
   */
  canOwn(assetType, actions, fields = "any") {
    return this.can(assetType, actions, fields, "own");
  }

  /**
   * Check if a user can do some action to even one asset of type X under any condition
   */
  canSome(assetType, actions, fields = "any") {
    return this.can(assetType, actions, fields, "some");
  }

  /**
   * Quick can test to see if the user can create a record
   * @param assetType
   * @returns {boolean}
   */
  canCreate(assetType) {
    return this.can(assetType, "create", "any", "some");
  }

  /**
   * Quick can test to see if the user can delete a record
   * @param assetType
   * @param level
   * @returns {boolean}
   */
  canDelete(assetType, level = "some") {
    return this.can(assetType, "delete", "any", level);
  }

  /**
   * Quick can test to see if the user can update records
   * @param assetType
   * @returns {boolean}
   */
  canUpdate(assetType, fields = "*", level = "some") {
    return this.can(assetType, "update", fields, level);
  }

  /**
   * Quick can test to see if the user can read records
   * @param assetType
   * @returns {boolean}
   */
  canRead(assetType, fields = "*", level = "some") {
    return this.can(assetType, "read", fields, level);
  }

  /**
   * Quick can test to see if the user can read specific records
   * @param assetType
   * @returns {boolean}
   */
  canReadSpecific(assetType, fields = "*", level = "some") {
    return this.can(assetType, "readSpecific", fields, level);
  }

  /**
   * Quick can test to see if the user can list records
   * @param assetType
   * @returns {boolean}
   */
  canList(assetType, fields = "*", level = "some") {
    return this.can(assetType, "list", fields, level);
  }

  /**
   * Quick can test to see if the user can list record versions
   * @param assetType
   * @returns {boolean}
   */
  canListVersions(assetType, fields = "*", level = "some") {
    return this.can(assetType, "listVersions", fields, level);
  }

  /**
   * Quick can test to see if the user can create record version
   * @param assetType
   * @returns {boolean}
   */
  canCreateVersion(assetType, fields = "*", level = "some") {
    return this.can(assetType, "createVersion", fields, level);
  }

  /**
   * Quick can test to see if the user can delete record version
   * @param assetType
   * @returns {boolean}
   */
  canDeleteVersion(assetType, level = "some") {
    return this.can(assetType, "deleteVersion", "any", level);
  }

  /**
   * Quick can test to see if the user can read record version
   * @param assetType
   * @returns {boolean}
   */
  canReadVersion(assetType, fields = "*", level = "some") {
    return this.can(assetType, "readVersion", fields, level);
  }

  /**
   * Quick can test to see if the user can read a specific record version
   * @param assetType
   * @returns {boolean}
   */
  canReadSpecificVersion(assetType, fields = "*", level = "some") {
    return this.can(assetType, "readSpecificVersion", fields, level);
  }

  /**
   * Quick can test to see if the user can update record version
   * @param assetType
   * @returns {boolean}
   */
  canUpdateVersion(assetType, fields = "*", level = "some") {
    return this.can(assetType, "updateVersion", fields, level);
  }

  /**
   * Quick can test to see if the user can accept record version
   * @param assetType
   * @returns {boolean}
   */
  canAcceptVersion(assetType, fields = "*", level = "some") {
    return this.can(assetType, "acceptVersion", fields, level);
  }

  /**
   * proxy for getUserGroups
   */
  getGroups() {
    return this.getUserGroups();
  }

  /**
   * proxy for getUserGroupIds
   */
  getGroupIds() {
    return this.getUserGroups();
  }

  /**
   * Get groups (aliases array) of the current user.
   * For guests returns ['public']
   * @returns {*}
   */
  getUserGroups() {
    return [...this.userGroups];
  }

  /**
   * Get groups (id array) of the current user.
   * For guests returns ['public']
   * @returns {*}
   */
  getUserGroupIds() {
    return [...this.userGroupIds];
  }

  /**
   * Check if the current user belongs to a specific group (by alias)
   * @param groupAlias - string
   * @returns {boolean}
   */
  hasGroup(groupAlias) {
    return this.userGroups.includes(groupAlias);
  }

  /**
   * Check if the current user belongs to a specific group (by alias)
   * @param groupAlias - string
   * @returns {boolean}
   */
  hasAnyGroup(groupAliases) {
    let verdict = false;
    this.userGroups.forEach((group) => {
      if (groupAliases.includes(group)) {
        verdict = true;
        return false;
      }
    });
    return verdict;
  }
}
